import { ChevronLeft, ChevronRight, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'

interface OtherProgram {
  id: string
  title: string
  image: string
  href: string
}

const GMESAfrica = () => {
  const otherPrograms: OtherProgram[] = [
    {
      id: 'eo-africa',
      title: 'EO Africa Initiative',
      image: 'https://res.cloudinary.com/dlwegkhme/image/upload/v1757447591/ee190e62c99c30d9e3f1c15d043344c855ae79d6_rabrvd.png',
      href: '/programmes/eo-africa-initiative'
    }
  ]

  const tags = ['GMES & Africa', 'Earth Observation', 'Sustainable Development', 'Environmental Monitoring']

  const handleShare = (platform: string) => {
    const url = window.location.href
    const title = 'GMES and Africa - African Space Agency'

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
        break
      case 'instagram':
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(url)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Programmes', href: '/programmes/programs' },
    { label: 'GMES and Africa', href: '/programmes/gmes-africa', active: true }
  ]

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <div className=" mb-5  ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content Container */}
      <div className="space-y-16">
        {/* Hero Section */}
        <div className="space-y-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
            GMES and Africa
          </h1>

          <div className="space-y-2">
            <img
              src="https://res.cloudinary.com/dlwegkhme/image/upload/v1755761598/9a5c7090884d5e6f246f1c212feaa613c6411912_sgt3cj.png"
              alt="GMES and Africa satellite monitoring system showing environmental data across the continent"
              className="w-full h-64 sm:h-80 md:h-96 lg:h-[502px] object-cover rounded"
            />
            <p className="text-base font-medium font-satoshi text-gray-600">
              GMES and Africa satellite monitoring system showing environmental data across the continent
            </p>
          </div>
        </div>

        {/* Content and Sidebar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1">
            <div className="space-y-12">
              {/* Article Content */}
              <div className="space-y-10">
                {/* Introduction */}
                <div className="space-y-6">
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    The GMES & Africa programme is designed to enhance the capacity of African institutions to utilise Earth observation (EO) technologies for sustainable development. The programme reflects a critical partnership between Africa and Europe, emphasising technology transfer, capacity building, and sustainable development through Earth observation.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    GMES & Africa works across 47 African countries, delivering data and services through E-stations and Regional Centers of Excellence and involving multiple academic, governmental, and scientific institutions as consortia. During the first phase, which started in December 2016 and ended in December 2021, the programme worked with 12 consortia, with at least two in each African region. The programme's second phase kicked off in January 2022 and is currently working with eight consortia.
                  </p>
                </div>

                {/* Background Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Background
                  </h2>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    The GMES and Africa Support Programme is a collaborative initiative between the European Union and the African Union, with a budget of EUR 30 million (USD 34 million) for its first phase. It was first introduced on December 7, 2007, during the 2nd EU-Africa Summit in Lisbon, Portugal.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    However, the programme's full-scale implementation only commenced nearly a decade later. The initiative builds upon the success of the African Monitoring of the Environment for Sustainable Development (AMESD) and Monitoring of Environment and Security in Africa (MESA) programmes.
                  </p>

                  <div className="space-y-4">
                    <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                      GMES & Africa is the natural progression designed to improve access to Earth Observation data and services in Africa. It aligns with the Agenda 2063 of the African Union, aiming to foster socioeconomic development, climate resilience, and sustainable resource management across the continents. The overarching goal of GMES & Africa is to empower African policymakers, scientists, and communities to use Earth observation data to:
                    </p>
                    <ul className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed ml-4 space-y-1 list-none">
                      <li>Improve environmental governance</li>
                      <li>Manage natural resources</li>
                      <li>Enhance disaster preparedness and resilience</li>
                      <li>Support sustainable development and climate change adaptation</li>
                      <li>Promote data sharing and scientific collaboration across the continent.</li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <p className="text-lg font-medium font-satoshi text-gray-900 leading-relaxed">
                      Scopes across two primary thematic areas:
                    </p>
                    <div className="space-y-4">
                      <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                        <span className="font-medium">Natural Resources, Water Management, and Marine & Coastal Areas:</span> This includes monitoring ecosystems, climate resilience, water resources, and coastal/marine health.
                      </p>
                      <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                        <span className="font-medium">Disaster Risk Reduction:</span> Earth observation technologies help identify areas prone to floods, droughts, and other natural disasters, allowing for more efficient responses.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Progress and Impact Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Progress and Impact
                  </h2>

                  <div className="space-y-4">
                    <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                      Since its inception, it has made significant strides in implementing Earth observation systems for practical use:
                    </p>
                    <div className="space-y-4">
                      <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                        In Phase One, the programme developed regional workshops, trained local experts, and installed E-stations, providing access to satellite data and Copernicus services. By its conclusion, Phase One had reached about 45 African countries.
                      </p>
                      <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                        Phase Two, launched in 2023, focuses on enhancing local capacity and expanding Earth observation applications in agriculture, water management, urbanisation, and climate monitoring. The second phase also includes disaster risk management improvements, offering broader, data-driven decision-making capabilities for African governments and regional bodies.
                      </p>
                      <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                        The programme has already contributed to combating deforestation, improving productivity, and monitoring coastal environments, especially in areas highly vulnerable to climate change.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Future Outlook Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Future Outlook
                  </h2>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    For Phase Two, the European Commission has committed EUR 24.65 million (USD 28 million) in support, and the programme will run until December 2025. Continued partnership with Copernicus ensures that Africa remains a beneficiary of the latest advancements in Earth observation and satellite technology, with a clear emphasis on supporting Africa's broader sustainable development goals. The programme's success demonstrates the growing importance of space technologies in addressing Africa's challenges, from environmental conservation to disaster risk reduction.
                  </p>
                </div>

                {/* Additional Image */}
                <div className="space-y-2">
                  <img
                    src="https://res.cloudinary.com/dlwegkhme/image/upload/v1757447873/e9f12f6b1c8dafe7bb1e3c37e72ca1169e3c475d_mvrywv.png"
                    alt="GMES Africa data monitoring stations and satellite networks"
                    className="w-full h-96 object-cover rounded"
                  />
                  <p className="text-base font-medium font-satoshi text-gray-600">
                    GMES Africa data monitoring stations and satellite networks
                  </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-4">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-6 py-1.5 bg-gray-50 rounded-full text-base font-medium font-satoshi text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Sharing */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="text-lg font-normal font-pt-serif text-gray-600">
                  SHARE
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                    aria-label="Share on Instagram"
                  >
                    <Instagram className="w-full h-full" />
                  </button>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <button className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors opacity-0 pointer-events-none">
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-base font-medium font-satoshi text-gray-900">
                    Previous
                  </span>
                </button>

                <button className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors">
                  <span className="text-base font-medium font-satoshi text-gray-900">
                    Next
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 order-2">
            <div className="space-y-10">
              {/* Other Programmes */}
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                  OTHER PROGRAMMES
                </h3>

                <div className="space-y-8">
                  {otherPrograms.map((program) => (
                    <a
                      key={program.id}
                      href={program.href}
                      className="group block"
                    >
                      <article className="space-y-4">
                        <div className="w-full h-72">
                          <img
                            src={program.image}
                            alt={program.title}
                            className="w-full h-full object-cover rounded group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h4 className="text-lg font-normal font-pt-serif text-gray-900 leading-relaxed group-hover:text-wine-100 transition-colors">
                          {program.title}
                        </h4>
                      </article>
                    </a>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              <RecentArticles />

              {/* Newsletter */}
              <Newsletter />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GMESAfrica